// @flow

// Libs
import _map from 'lodash/map'
import _every from 'lodash/every'
import _size from 'lodash/size'
import _get from 'lodash/get'

// Effect Managers
import { call, fork, put, race, select, take } from 'redux-saga/effects'

// Utils
import { convertRecommendationToBackendUnits } from 'hfty-recommendations'

// Operator
import operator from '../operator'
import validateOperation from './validateOperation'

// Controller
import controller from 'hfty-ctrl-recommendations'

// Selectors
import { currentUserUnits$ } from 'hfty-users'
import { crops$ } from 'fc-model-crops'
import { fertilizers$ } from 'hfty-fertilizers'

// Constants
import {
  // Model Properties
  RECOMMENDATION_FERTILIZER_SET,
  PROP_ELEMENT_PICK,
  PROP_ELEMENT_SPLIT,
  PROP_PERCENT_SPLIT,
  ELEMENT_SPLIT_TYPE,
  PROP_TANK_NUMBER,
} from 'hfty-model-recommendations'

// Types
import type {
  RecommendationOperation,
} from '../../types'
import type {
  Recommendation,
} from 'hfty-model-recommendations/types'
type OperationsArgs = {
  operations$: (state: Object) => Array<RecommendationOperation>
}

// Exports
export default function *watchOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  while (true) {
    yield take([operator.actionTypes.add(), operator.actionTypes.edit(), operator.actionTypes.delete()])
    yield fork(handleOperations, {operations$})
  }
}

export function *handleOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  let done = false

  while (!done) {
    const effect = yield race({
      submit: take(operator.actionTypes.submitOperations()),
      cancel: take(operator.actionTypes.cancelOperations()),
    })

    if (effect.submit) {
      done = yield call(handleSubmitOperations, {operations$})
    } else {
      done = yield call(handleCancelOperations, {operations$})
    }
  }
}

export function *handleCancelOperations ({operations$}: OperationsArgs): Generator<*, boolean, *> {
  const operations = yield select(operations$)
  const action = yield operator.actionCreators.cancelOperations(operations)
  yield put(action)
  return true
}

export function *handleSubmitOperations ({operations$}: OperationsArgs): Generator<*, boolean, *> {
  const operations = yield select(operations$)

  const nextOperations: Array<RecommendationOperation> =
  yield _map(operations, function (operation: RecommendationOperation): Object {
      return call(handleSubmitOperation, operation)
    })

  return _every(nextOperations, function (operation: RecommendationOperation): boolean {
    return operation.success
  })
}

export function *handleSubmitOperation (operation: RecommendationOperation): Generator<*, RecommendationOperation, *> {
  const validatedOperation = yield call(validateOperation, operation)
  if (_size(validatedOperation.errors)) {
    return validatedOperation
  }

  const currentUserUnits = yield select(currentUserUnits$)
  const crops = yield select(crops$)
  const fertilizers = yield select(fertilizers$)

  // Handle blank and zero splits (convert to 0 for percent and null for element)
  const oldModel: Recommendation = operation.model
  const oldPrescriptions = oldModel[RECOMMENDATION_FERTILIZER_SET]
  const newPrescriptions = _map(oldPrescriptions, (prescription: Object): Object => {
    const elementSplit = _get(oldModel, `[${ELEMENT_SPLIT_TYPE}][${prescription[PROP_ELEMENT_PICK]}]`)
    delete prescription[PROP_TANK_NUMBER]

    if (elementSplit === PROP_ELEMENT_SPLIT) {
      if (
        prescription[PROP_ELEMENT_SPLIT] === '' ||
        prescription[PROP_ELEMENT_SPLIT] === null ||
        Number(prescription[PROP_ELEMENT_SPLIT]) === 0
      ) {
        return {
          ...prescription,
          [PROP_ELEMENT_SPLIT]: null,
        }
      } else {
        return {
          ...prescription,
        }
      }
    } else if (elementSplit === PROP_PERCENT_SPLIT) {
      if (prescription[PROP_PERCENT_SPLIT] === '' || prescription[PROP_PERCENT_SPLIT] === null) {
        return {
          ...prescription,
          [PROP_PERCENT_SPLIT]: 0,
        }
      } else {
        return {
          ...prescription,
        }
      }
    } else {
      return {
        ...prescription,
        [PROP_PERCENT_SPLIT]: 0,
        [PROP_ELEMENT_SPLIT]: null,
      }
    }
  })

  const model = {
    ...oldModel,
    [RECOMMENDATION_FERTILIZER_SET]: newPrescriptions,
  }

  const proc = operation.operation === 'ADD'
    ? controller.create
    : operation.operation === 'EDIT'
      ? controller.update
      : controller.delete
  const { error, response } = yield call([controller, proc], {
    model: convertRecommendationToBackendUnits(model, crops, currentUserUnits, fertilizers),
  })

  if (error) {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: false,
      errors: response,
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    return nextOperation
  } else {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: true,
    }

    const action = operator.actionCreators.cancelOperation(nextOperation)
    yield put(action)

    return nextOperation
  }
}
